/**
 * FormRenderer - Unified form rendering component
 *
 * Task: T-P4-D2-F4b-5, T-P4-D5-F2-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Dynamically renders forms based on field definitions. Handles:
 * - Rendering correct field component for each field type
 * - Managing form values state
 * - Client-side validation on blur and submit
 * - Auto-save on field blur (debounced in parent, unless disabled)
 * - Auto-save every 60 seconds if form is dirty (T-P4-D5-F2-4)
 * - Merging server errors with client errors
 * - Loading/submitting states
 *
 * Auto-save strategy (T-P4-D5-F2-4):
 * - Does NOT save on every keystroke (reduces backend load)
 * - Saves on field blur (when user finishes typing in a field)
 * - Saves every 60 seconds if form is dirty (catches long typing sessions)
 * - Dirty = values changed since last save
 *
 * FormRenderer is stateless regarding "which form" - it receives a form
 * definition and renders it. The parent component (DetailsPage) determines
 * which form to show.
 */

import { useState, useEffect, useCallback, useRef, useMemo, FormEvent } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type {
  FormDefinition,
  FormField,
  FormResponseInput,
  FieldError,
} from '../../types/forms';
import { validateField, validateAllFields, isFormValid, computeVisibleFieldIds } from '../../utils/formValidation';
// Import field components directly to avoid circular import through barrel
import { TextField } from './TextField';
import { EmailField } from './EmailField';
import { TextareaField } from './TextareaField';
import { NumberField } from './NumberField';
import { DateField } from './DateField';
import { SelectField } from './SelectField';
import { CheckboxField } from './CheckboxField';
import { RadioField } from './RadioField';
import { RatingField } from './RatingField';
import { SignatureField } from './SignatureField';
import { FieldPartsProvider } from './fieldParts';
import styles from './FormRenderer.module.css';

// ============================================
// Constants
// ============================================

/** Interval between auto-saves when form is dirty (T-P4-D5-F2-4) */
const AUTOSAVE_INTERVAL_MS = 60_000; // 60 seconds

// ============================================
// Types
// ============================================

export interface FormRendererProps {
  /** Form definition from API */
  form: FormDefinition;
  /** Called when form is submitted with all field values */
  onSubmit: (responses: FormResponseInput[]) => void;
  /** Called when field loses focus (for auto-save) */
  onSave: (responses: FormResponseInput[]) => void;
  /** Whether form is currently being submitted */
  isSubmitting: boolean;
  /** Server-side validation errors */
  serverErrors?: FieldError[];
  /** Disable auto-save (for registration forms where backend doesn't support it) */
  disableAutoSave?: boolean;
  /**
   * CWR 2.5 (D4) — opt into the client-booking field restyle. Emits
   * `data-form-appearance="clientBooking"` on the form root; the new field CSS
   * scopes beneath it. Default ('default') is byte-for-byte unchanged.
   */
  appearance?: 'default' | 'clientBooking';
  /**
   * CWR 2.5 (F) — opt-in footer slot rendered INSIDE the <form> in place of the
   * default submit button. The slot MUST contain a real type="submit" button, so
   * native submission + validation + next-step routing are preserved. When omitted,
   * the default button renders (FormRequestPage passes none).
   */
  renderActions?: (state: { formReady: boolean; isSubmitting: boolean }) => ReactNode;
  /**
   * web-account-pages D6 — called on every change with the visible fields'
   * answers by field id, the whole set (a sent form saves as the client types).
   * Independent of `onSave` and `disableAutoSave`.
   */
  onAnswersChange?: (answers: Record<string, unknown>) => void;
  /**
   * web-studio-sites D6 — attributes for the questions' container alone (the website's `fields` part), so a
   * site can style the questions apart from the button `renderActions` draws after them. Omitted, nothing changes.
   */
  fieldsAttributes?: Readonly<Record<string, string>>;
  /**
   * form-embed D8 — name each question's pieces as the website's published parts (`question`, `label`, `hint`,
   * `choice` with `data-state="selected"`). Omitted, nothing changes.
   */
  markParts?: boolean;
  /**
   * form-embed D8 — hide the questions but keep them drawn, so their answers stay: the website form's second
   * step shows only what `renderActions` draws. Omitted, nothing changes.
   */
  fieldsHidden?: boolean;
}

// ============================================
// Icons
// ============================================

function ArrowIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

// ============================================
// Utility Functions
// ============================================

/**
 * Get the default value for a field based on its type.
 */
/**
 * Non-interactive field types that should not appear in submit or autosave payloads.
 */
function isSubmittableField(field: FormField): boolean {
  return field.type !== 'CONTENT';
}

function getDefaultValue(field: FormField): unknown {
  switch (field.type) {
    case 'CONTENT':
      return undefined; // non-interactive, never submitted
    case 'CHECKBOX':
      // Multi-select (with options) defaults to empty array
      // Consent (no options) defaults to false
      return field.options && field.options.length > 0 ? [] : false;
    case 'RATING':
      return undefined;
    case 'SIGNATURE':
      return '';
    case 'NUMBER':
    case 'TEXT':
    case 'EMAIL':
    case 'TEXTAREA':
    case 'DATE':
    case 'SELECT':
    case 'RADIO':
    default:
      return '';
  }
}

/**
 * Build initial values from form definition and saved data.
 */
function buildInitialValues(form: FormDefinition): Record<string, unknown> {
  const initial: Record<string, unknown> = {};
  for (const field of form.fields) {
    initial[field.fieldId] =
      form.savedData?.[field.fieldId] ?? getDefaultValue(field);
  }
  return initial;
}

/**
 * Build responses array from current form values.
 */
function buildResponses(
  fields: FormField[],
  values: Record<string, unknown>
): FormResponseInput[] {
  return fields
    .map((field) => ({
      fieldId: field.fieldId,
      value: values[field.fieldId] ?? getDefaultValue(field),
    }))
    .filter((r): r is FormResponseInput => r.value !== undefined);
}

/**
 * Build partial responses (only non-empty values) for auto-save.
 */
function buildPartialResponses(
  values: Record<string, unknown>
): FormResponseInput[] {
  return Object.entries(values)
    .filter(([_, v]) => {
      // Filter out empty values
      if (v === undefined || v === null || v === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    })
    .map(([fieldId, value]) => ({
      fieldId,
      value: value as string | boolean | string[] | number,
    }));
}

// ============================================
// Component
// ============================================

/**
 * FormRenderer component for rendering dynamic forms.
 *
 * Usage:
 * ```tsx
 * <FormRenderer
 *   form={formDefinition}
 *   onSubmit={handleSubmit}
 *   onSave={handleSave}
 *   isSubmitting={mutation.isPending}
 *   serverErrors={mutation.error?.fieldErrors}
 *   disableAutoSave={form.formType === 'REGISTRATION'}
 * />
 * ```
 */
export function FormRenderer({
  form,
  onSubmit,
  onSave,
  isSubmitting,
  serverErrors,
  disableAutoSave = false,
  appearance = 'default',
  renderActions,
  onAnswersChange,
  fieldsAttributes,
  markParts = false,
  fieldsHidden,
}: FormRendererProps) {
  // If form is already submitted, return null and let parent handle
  if (form.submission) {
    return null;
  }

  // Form values state - initialized from savedData if available
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    buildInitialValues(form)
  );

  // Ref to always have access to latest values (avoids stale closure in handleBlur)
  // When checkbox change and blur fire in rapid succession, the blur callback
  // would see stale state. This ref ensures we always have the current values.
  const valuesRef = useRef<Record<string, unknown>>(values);

  // Keep ref in sync with state
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  // Compute visible field IDs based on showWhen conditions
  const visibleFieldIds = useMemo(
    () => computeVisibleFieldIds(form.fields, values),
    [form.fields, values]
  );

  // Clear-on-hide: reset values for fields that transition visible → hidden
  const prevVisibleRef = useRef<Set<string>>(visibleFieldIds);

  useEffect(() => {
    const prev = prevVisibleRef.current;
    const current = visibleFieldIds;

    for (const fieldId of prev) {
      if (!current.has(fieldId)) {
        const field = form.fields.find((f) => f.fieldId === fieldId);
        if (field) {
          const defaultVal = getDefaultValue(field);
          setValues((v) => ({ ...v, [fieldId]: defaultVal }));
        }
      }
    }

    prevVisibleRef.current = current;
  }, [visibleFieldIds, form.fields]);

  // Track last saved values for dirty detection (T-P4-D5-F2-4)
  // Used by interval-based autosave to know if form has changed since last save
  const lastSavedValuesRef = useRef<Record<string, unknown>>(
    buildInitialValues(form)
  );

  const onAnswersChangeRef = useRef(onAnswersChange);
  onAnswersChangeRef.current = onAnswersChange;
  const fieldsRef = useRef(form.fields);
  fieldsRef.current = form.fields;

  // Stable ref for onSave to avoid interval recreation
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  // Interval-based autosave (T-P4-D5-F2-4)
  // Saves every 60 seconds if form is dirty (values changed since last save)
  // This catches cases where user types but doesn't blur (e.g., stays in same field)
  useEffect(() => {
    // Skip if autosave is disabled (registration forms)
    if (disableAutoSave) return;

    const intervalId = setInterval(() => {
      const currentValues = valuesRef.current;
      const lastSaved = lastSavedValuesRef.current;

      // Check if values have changed (simple JSON comparison)
      const isDirty = JSON.stringify(currentValues) !== JSON.stringify(lastSaved);

      if (isDirty) {
        const currentVisibleIds = computeVisibleFieldIds(form.fields, currentValues);
        const contentFieldIds = new Set(form.fields.filter((f) => !isSubmittableField(f)).map((f) => f.fieldId));
        const responses = buildPartialResponses(currentValues)
          .filter((r) => currentVisibleIds.has(r.fieldId) && !contentFieldIds.has(r.fieldId));
        if (responses.length > 0) {
          onSaveRef.current(responses);
          // Update last saved snapshot
          lastSavedValuesRef.current = { ...currentValues };
        }
      }
    }, AUTOSAVE_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [disableAutoSave, form.fields]); // Recreate if disableAutoSave or fields change

  // Reset lastSavedValuesRef when form changes
  useEffect(() => {
    lastSavedValuesRef.current = buildInitialValues(form);
  }, [form.formId]);

  // Touched fields (for showing validation on blur)
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Keep client and server errors separate to avoid stale merging issues
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrorMap, setServerErrorMap] = useState<Record<string, string>>(
    {}
  );

  // Reset all state when form.formId changes (e.g., navigating to next form)
  useEffect(() => {
    const initialValues = buildInitialValues(form);
    setValues(initialValues);
    valuesRef.current = initialValues; // Keep ref in sync
    prevVisibleRef.current = computeVisibleFieldIds(form.fields, initialValues);
    setTouched(new Set());
    setClientErrors({});
    setServerErrorMap({});
  }, [form.formId]);

  // Sync server errors when they change (e.g., after failed submission)
  // This replaces the previous server errors entirely (not additive)
  useEffect(() => {
    if (serverErrors && serverErrors.length > 0) {
      const errorMap: Record<string, string> = {};
      serverErrors.forEach((e) => {
        errorMap[e.fieldId] = e.error;
      });
      setServerErrorMap(errorMap);
      // Mark all server error fields as touched so they display
      setTouched((prev) => {
        const next = new Set(prev);
        serverErrors.forEach((e) => next.add(e.fieldId));
        return next;
      });
    } else {
      // Clear server errors when serverErrors prop becomes empty/undefined
      setServerErrorMap({});
    }
  }, [serverErrors]);

  // Merge client and server errors for display (server errors take precedence)
  const getFieldError = useCallback(
    (fieldId: string): string | undefined => {
      return serverErrorMap[fieldId] ?? clientErrors[fieldId];
    },
    [clientErrors, serverErrorMap]
  );

  // Handle field value change
  // Triggers auto-save on every change (debounced in parent hook)
  // This is necessary because checkboxes/radios/selects don't fire blur reliably
  const handleChange = useCallback((fieldId: string, value: unknown) => {
    // Update ref synchronously BEFORE async state update
    // This ensures handleBlur always sees the latest value even if blur fires
    // immediately after change (e.g., checkbox click then back button)
    const newValues = { ...valuesRef.current, [fieldId]: value };
    valuesRef.current = newValues;

    setValues((prev) => ({ ...prev, [fieldId]: value }));
    if (onAnswersChangeRef.current) {
      const fields = fieldsRef.current;
      const visible = computeVisibleFieldIds(fields, newValues);
      const answers: Record<string, unknown> = {};
      for (const f of fields) {
        if (visible.has(f.fieldId) && isSubmittableField(f) && newValues[f.fieldId] !== undefined) answers[f.fieldId] = newValues[f.fieldId];
      }
      onAnswersChangeRef.current(answers);
    }
    // Clear both client and server errors when user starts editing
    setClientErrors((prev) => {
      if (prev[fieldId]) {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      }
      return prev;
    });
    setServerErrorMap((prev) => {
      if (prev[fieldId]) {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      }
      return prev;
    });

    // NOTE: Auto-save is NOT triggered on every change (T-P4-D5-F2-4 review).
    // Instead, auto-save happens:
    // 1. On field blur (handleBlur below)
    // 2. Via 60-second interval (when dirty)
    // This reduces unnecessary API calls and backend load.
  }, []);

  // Handle field blur - validate and trigger auto-save
  // Uses valuesRef instead of values state to avoid stale closure when
  // change and blur fire in rapid succession (e.g., checkbox click)
  const handleBlur = useCallback(
    (field: FormField) => {
      setTouched((prev) => new Set(prev).add(field.fieldId));

      // Read from ref to get the latest value (avoids stale closure)
      const currentValues = valuesRef.current;

      // Validate the field
      const error = validateField(field, currentValues[field.fieldId]);
      if (error) {
        setClientErrors((prev) => ({ ...prev, [field.fieldId]: error }));
      }

      // Skip auto-save for registration forms (backend doesn't support)
      if (disableAutoSave) return;

      // Trigger auto-save with current values from ref
      const currentVisibleIds = computeVisibleFieldIds(form.fields, currentValues);
      const contentIds = new Set(form.fields.filter((f) => !isSubmittableField(f)).map((f) => f.fieldId));
      const responses = buildPartialResponses(currentValues)
        .filter((r) => currentVisibleIds.has(r.fieldId) && !contentIds.has(r.fieldId));
      if (responses.length > 0) {
        onSave(responses);
        // Update last saved snapshot to prevent interval from double-saving (T-P4-D5-F2-4)
        lastSavedValuesRef.current = { ...currentValues };
      }
    },
    [onSave, disableAutoSave, form.fields]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      // Validate all fields
      const allErrors = validateAllFields(form.fields, values);

      if (allErrors.length > 0) {
        // Set client errors and mark all fields as touched
        const errorMap: Record<string, string> = {};
        allErrors.forEach((err) => {
          errorMap[err.fieldId] = err.error;
        });
        setClientErrors(errorMap);
        setTouched(new Set(form.fields.map((f) => f.fieldId)));

        // Auto-focus the FIRST invalid field (in field order) so the user lands on
        // exactly what needs filling: scroll its wrapper into view + focus the input.
        // Groups (radio/checkbox) have no single focusable input, so they just scroll.
        const firstInvalid = form.fields.find(
          (f) => visibleFieldIds.has(f.fieldId) && errorMap[f.fieldId] !== undefined,
        );
        if (firstInvalid) {
          requestAnimationFrame(() => {
            document
              .querySelector(`[data-field-id="${CSS.escape(firstInvalid.fieldId)}"]`)
              ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
            document.getElementById(firstInvalid.fieldId)?.focus({ preventScroll: true });
          });
        }

        // Haptic feedback for error
        if (navigator.vibrate) {
          navigator.vibrate([50, 50, 50]);
        }
        return;
      }

      // Build and submit responses — only visible, submittable fields
      const visibleFields = form.fields.filter((f) => visibleFieldIds.has(f.fieldId) && isSubmittableField(f));
      const responses = buildResponses(visibleFields, values);
      onSubmit(responses);
    },
    [form.fields, values, onSubmit, visibleFieldIds]
  );

  // Render field based on type
  const renderField = useCallback(
    (field: FormField, index: number) => {
      // showWhen visibility gate — use original index for animation stability
      if (!visibleFieldIds.has(field.fieldId)) return null;

      const animationStyle: CSSProperties = {
        animationDelay: `${index * 50}ms`,
      };

      const error = touched.has(field.fieldId)
        ? getFieldError(field.fieldId)
        : undefined;

      const commonProps = {
        field,
        onBlur: () => handleBlur(field),
        error,
        style: animationStyle,
      };

      switch (field.type) {
        case 'TEXT':
          return (
            <TextField
              key={field.fieldId}
              {...commonProps}
              value={(values[field.fieldId] as string) ?? ''}
              onChange={(v) => handleChange(field.fieldId, v)}
            />
          );

        case 'EMAIL':
          return (
            <EmailField
              key={field.fieldId}
              {...commonProps}
              value={(values[field.fieldId] as string) ?? ''}
              onChange={(v) => handleChange(field.fieldId, v)}
            />
          );

        case 'TEXTAREA':
          return (
            <TextareaField
              key={field.fieldId}
              {...commonProps}
              value={(values[field.fieldId] as string) ?? ''}
              onChange={(v) => handleChange(field.fieldId, v)}
            />
          );

        case 'NUMBER':
          return (
            <NumberField
              key={field.fieldId}
              {...commonProps}
              value={(values[field.fieldId] as string) ?? ''}
              onChange={(v) => handleChange(field.fieldId, v)}
            />
          );

        case 'DATE':
          return (
            <DateField
              key={field.fieldId}
              {...commonProps}
              value={(values[field.fieldId] as string) ?? ''}
              onChange={(v) => handleChange(field.fieldId, v)}
            />
          );

        case 'SELECT':
          // form-embed D13: a Dropdown set to Chips is answered like a radio, one pill per option.
          if (field.display === 'chips') {
            return (
              <RadioField
                key={field.fieldId}
                {...commonProps}
                value={(values[field.fieldId] as string) ?? ''}
                onChange={(v) => handleChange(field.fieldId, v)}
              />
            );
          }
          return (
            <SelectField
              key={field.fieldId}
              {...commonProps}
              value={(values[field.fieldId] as string) ?? ''}
              onChange={(v) => handleChange(field.fieldId, v)}
            />
          );

        case 'CHECKBOX':
          return (
            <CheckboxField
              key={field.fieldId}
              {...commonProps}
              value={
                (values[field.fieldId] as boolean | string[]) ??
                (field.options && field.options.length > 0 ? [] : false)
              }
              onChange={(v) => handleChange(field.fieldId, v)}
            />
          );

        case 'RADIO':
          return (
            <RadioField
              key={field.fieldId}
              {...commonProps}
              value={(values[field.fieldId] as string) ?? ''}
              onChange={(v) => handleChange(field.fieldId, v)}
            />
          );

        case 'RATING':
          return (
            <RatingField
              key={field.fieldId}
              {...commonProps}
              value={values[field.fieldId] as number | undefined}
              onChange={(v) => handleChange(field.fieldId, v)}
            />
          );

        case 'SIGNATURE':
          return (
            <SignatureField
              key={`${form.formId}:${field.fieldId}`}
              {...commonProps}
              value={(values[field.fieldId] as string) ?? ''}
              onChange={(v) => handleChange(field.fieldId, v)}
            />
          );

        case 'CONTENT':
          return (
            <div key={field.fieldId} className={styles.contentBlock} style={animationStyle}>
              {field.label && <div className={styles.contentHeading}>{field.label}</div>}
              {field.helpText && <div className={styles.contentBody}>{field.helpText}</div>}
            </div>
          );

        default:
          return null;
      }
    },
    [values, touched, getFieldError, handleChange, handleBlur, visibleFieldIds, form.formId]
  );

  // Check if form is ready to submit (all required fields valid)
  const formReady = isFormValid(form.fields, values);

  // Determine submit button text based on form readiness
  // Registration forms: "Complete Registration" when not ready, "Continue" when ready
  // Other forms: Always "Continue"
  const submitButtonText =
    form.formType === 'REGISTRATION'
      ? formReady ? 'Continue' : 'Complete Registration'
      : 'Continue';

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit}
      noValidate
      data-form-appearance={appearance === 'clientBooking' ? 'clientBooking' : undefined}
    >
      <div className={styles.fieldsContainer} hidden={fieldsHidden} {...fieldsAttributes}>
        <FieldPartsProvider value={markParts}>{form.fields.map((field, index) => renderField(field, index))}</FieldPartsProvider>
      </div>

      {/* CWR 2.5 (F): when a renderActions slot is provided (the DetailsPage floating
          bar), it OWNS the submit button; otherwise the default in-flow button renders. */}
      {renderActions ? (
        renderActions({ formReady, isSubmitting })
      ) : (
        <button
          type="submit"
          className={`${styles.submitBtn} ${isSubmitting ? styles.loading : ''} ${!formReady ? styles.notReady : ''}`}
          disabled={isSubmitting}
        >
          <span className={styles.btnText}>{submitButtonText}</span>
          {formReady && (
            <span className={styles.btnIcon}>
              <ArrowIcon />
            </span>
          )}
          <span className={styles.btnSpinner} aria-hidden="true" />
        </button>
      )}
    </form>
  );
}
